import type { HelpGuide } from '@types';

const guide = (title: string, introduction: string, tasks: [string, string[], string?][]): HelpGuide => ({
    reportType: 'HelpGuide', title, introduction,
    sections: tasks.map(([title, steps, note]) => ({
        title,
        content: steps.map((step, index) => `${index + 1}. ${step}`).join('\n') + (note ? `\n\n${note}` : '')
    }))
});

export const HELP_CONTENT: Record<string, HelpGuide> = {
    'admin-start': guide('Start here: admin and consultant tasks',
        'Use the Management Portal to manage clients and delivery, and the organization workspace to prepare work.', [
            ['Choose the right workspace', [
                'Sign in to the **Management Portal** with your staff account.',
                'Open **Organizations** and find the client you need.',
                'Open the organization’s working dashboard to prepare reports and frameworks.',
                'Use **Core Pillars Hub** and the left navigation to reach the required suite.',
                'Return to **Service Requests** in the Management Portal to review and publish delivery.',
                'Use the organization’s client-view option to check what the client can see.'
            ], 'The client preview is read-only. Management actions depend on your staff role and assigned organizations.']
        ]),
    'admin-organizations': guide('Management Portal: organizations and client access',
        'Maintain client details, consultant assignment, and delivery responsibility.', [
            ['Update an organization', [
                'Open **Organizations** in the Management Portal.',
                'Find the organization on the map or in **All Organizations**.',
                'Select **Manage** and check the organization name and owner.',
                'Update the profile, addresses, additional locations, and assigned consultant as needed.',
                'If the client has an internal security professional, enable that option and select an active user.',
                'Select **Save Organization** and confirm the updated details.',
                'Open the working dashboard or client preview using the available organization controls.'
            ], 'Use **Awaiting Consultant Assignment** to find unassigned clients. Super administrators can mark a **Test Organization**; service requests still use normal billing choices.'],
            ['Manage organization users', [
                'Open the organization’s **Organization Users** section.',
                'Select **Add User**, enter their details, and choose the appropriate role.',
                'Select **Send Invite** and check the confirmation.',
                'Use **Edit** to change an existing user’s available access controls.',
                'Use **Transfer Ownership** only after checking the recipient and confirmation.',
                'Check the client preview after changing access.'
            ]]
        ]),
    'admin-service-delivery': guide('Management Portal: review and deliver service requests',
        'Take a request from intake through reviewed delivery and completion.', [
            ['Review and accept a request', [
                'Open **Service Requests** in the Management Portal.',
                'Search by request reference, client, service, country, or consultant. Use **My Clients** when helpful.',
                'Select **View** and read the intake, attachments, priority, and requested delivery date.',
                'Enter consultant notes, the estimated delivery date, and the assigned consultant.',
                'Select **Request More Information** if evidence is missing, or **Accept Request** when ready.',
                'Open the service-specific workspace offered for the accepted request.'
            ], 'Read client responses in the request conversation. If revised evidence rebuilds a draft, validate and approve it again.'],
            ['Publish reviewed work', [
                'Complete the service workspace’s preparation, validation, and approval steps.',
                'Return to **Service Delivery** and read **Complete these steps to publish**.',
                'Add the delivery files, approved reports, or dashboard required for the service.',
                'Check the confidentiality setting and client-facing message.',
                'Use **Save Progress** if the delivery is unfinished.',
                'Select **Mark Delivered and Publish** when every listed requirement is satisfied.',
                'Check the client preview and **Intelligence Library** to confirm delivery is available.'
            ], 'For saved reports, use **Refresh**, select the required reports, and use **Add Reports to Client Library** when offered. Publication releases the work to the client.'],
            ['Bill or complete a delivered service', [
                'Open the delivered request and review its billing choice.',
                'Choose **Complete for free** when the request should finish without an invoice.',
                'For billable work, review **MSA Rate**, additional expenses, payment terms, and purchase-order details.',
                'Use **Save Draft** for an unfinished invoice, or **Send Invoice to Client** when ready.',
                'Review the **Invoices** tab and **Invoice Log** for billing progress.'
            ], 'Follow the free-trial instructions shown on the request. Test organizations may still have billable services.']
        ]),
    'admin-communication': guide('Management Portal: communication and support',
        'Follow client chats, support tickets, and feedback from Communication.', [
            ['Respond to client support', [
                'Open **Communication** in the Management Portal.',
                'Choose the available chat, support, or feedback view.',
                'Open the item and read the client’s history and request reference.',
                'Respond through the appropriate conversation or update the ticket using its available controls.',
                'Check the item’s status after saving.',
                'Review your profile’s available notification options when notifications need changing.'
            ], 'Use the service request’s conversation for evidence and questions tied to a particular delivery.']
        ]),
    'admin-platform-management': guide('Management Portal: platform management',
        'Authorized staff maintain products, MSA documents, and model settings.', [
            ['Update platform settings', [
                'Open **Platform Management** when your staff role permits it.',
                'Choose the relevant product, MSA, or model view.',
                'Open the intended item and review its current settings.',
                'Change the fields needed for the task.',
                'Save through the available control and check the resulting status.',
                'Review validation and model-retirement notices before making further changes.'
            ], 'Use **Users** for user and staff-account management. If a view is missing, check your staff permissions.']
        ]),
    'knowledgeHub': guide('Platform Guide: Knowledge Hub and Core Pillars Hub',
        'Choose the organization workspace and suite needed for your task.', [
            ['Open a suite', [
                'Open the client’s working dashboard from **Organizations**.',
                'Confirm the organization name before preparing work.',
                'Choose **Core Pillars Hub** in the left navigation.',
                'Select the required pillar or use a suite shortcut.',
                'Choose the task or workflow in that workspace.',
                'Return to **Home** or **Intelligence Library** to review existing work.'
            ]]
        ]),
    'industryHub': guide('Platform Guide: Industry Dashboards',
        'Find tools in a selected industry or pillar workspace.', [
            ['Choose a task', [
                'Open the relevant workspace from **Core Pillars Hub** or the left navigation.',
                'Check the workspace title and organization context.',
                'Select the suite or workflow matching the service request.',
                'Read the required inputs before entering information.',
                'Complete the workflow and review the report before saving or delivering it.'
            ]]
        ]),
    'suite-srm': guide('Suite Guide: Security Risk Management (SRM)',
        'Prepare frameworks, review components, and manage recorded security risks.', [
            ['Work on a framework', [
                'Open **Security Risk Management** in the working workspace.',
                'Open **SRM Command Center** and select a framework, or begin a new framework through the architect.',
                'Check the asset, location, scope, and framework status.',
                'Complete and review the required components.',
                'Review pending scenarios in **Risk Register** before accepting them.',
                'Follow the dashboard’s next steps and approval controls before activation.',
                'For client work, return to the service request and publish the approved delivery.'
            ], 'Delivered client framework dashboards are read-only. Preparation and review occur in the working workspace.']
        ]),
    'wf-integrated_threat_analysis': guide('Workflow: Integrated Threat Analysis Hub',
        'Prepare a threat assessment for a defined location, asset, or operational concern.', [
            ['Run and review an assessment', [
                'Open the Integrated Threat Analysis workflow from the relevant suite.',
                'Enter the location, asset, and threat context requested by the form.',
                'Complete the required inputs and start the analysis.',
                'Review the threats, evidence, uncertainty, and recommended actions.',
                'Save the reviewed result to the intended organization or framework using available controls.',
                'Review any resulting pending risk scenarios before approving them.'
            ]]
        ]),
    'wf-facility_asset_protection': guide('Workflow: Facility and Asset Protection Plan',
        'Prepare a site protection plan using the facility’s actual details.', [
            ['Prepare a protection plan', [
                'Open the Facility and Asset Protection workflow, or the accepted **S-02** workspace.',
                'Check the facility, complete address, operating context, and client objectives.',
                'Complete the required fields and supporting evidence.',
                'Generate the draft and review proposed controls against the site information.',
                'Resolve validation findings and approve the work where required.',
                'Save the reviewed plan, or publish it through the related request.',
                'Use **Start Automated Audit** on a plan that offers it to begin an FSAT assessment.'
            ]]
        ]),
    'wf-facility_risk_assessment': guide('Workflow: Facility Security Assessment Tool (FSAT)',
        'Record site observations and prepare a reviewed facility assessment.', [
            ['Complete an assessment', [
                'Open FSAT or the accepted **S-01 Facility Security Assessment** workspace.',
                'Confirm the company, facility, address, audit information, and supporting documents.',
                'Work through each assessment section and record observed conditions.',
                'Use **N/A** only when a control does not apply. Record limitations rather than guessing.',
                'Complete **Summary & Recommendations** and check missing fields or evidence.',
                'Generate or save the assessment using the available controls.',
                'For service delivery, validate, approve, and publish through the request.'
            ], 'Clients can open **View FSAT Documents** after submission. Review newly supplied evidence before releasing an updated assessment.']
        ]),
    'wf-global_asset_management': guide('Workflow: Strategic Asset Management',
        'Maintain assets and the primary location used by security workflows.', [
            ['Add or update an asset', [
                'Select **Manage Global Assets** in the working workspace.',
                'Open an asset or choose **Add New Critical Asset**.',
                'Enter the name, type, criticality, country, complete address, and notes.',
                'Check the location and select **Save Asset**.',
                'Use **Set as Primary** for the primary operating location when needed.',
                'Reopen the asset to confirm the saved information.'
            ], 'Only one primary profile is kept at a time. Clients use **View Global Assets** to read this information.']
        ]),
    'wf-srm_auto_architect': guide('Workflow: SRM Auto-Architect',
        'Create a framework draft, then complete its review and configuration.', [
            ['Build a framework draft', [
                'Open the SRM architect in the client’s working workspace.',
                'Check the organization, sector, location, and available primary profile.',
                'Enter the objectives, assets, and operating context.',
                'Review imported assets and remove anything outside the agreed scope.',
                'Select **Initialize Architect Protocol**.',
                'Review the generated framework and complete its required components.',
                'Follow the framework’s approval steps before activation.'
            ], 'A generated draft needs review. Check the location and scope before running further tools.']
        ]),
    'wf-audit_automation': guide('Workflow: Automated Audit Bridge',
        'Start a facility assessment from an available protection plan.', [
            ['Start the linked audit', [
                'Open the completed Facility and Asset Protection Plan.',
                'Select **Start Automated Audit** when available.',
                'Check the facility and context carried into the FSAT form.',
                'Correct incomplete details and add physical site observations.',
                'Complete the summary and recommendations.',
                'Save or generate the assessment using available controls.',
                'Review the framework and pending risks after saving.'
            ], 'Imported information is a starting point. Verify site conditions before completing the audit.']
        ]),
    'suite-cmp': guide('Suite Guide: Crisis Management & Resiliency',
        'Prepare crisis management, business resiliency, and emergency preparedness work.', [
            ['Prepare a plan', [
                'Open **Business Resiliency & Crises Management** in the working workspace.',
                'Choose the crisis, resiliency, or preparedness task matching the request.',
                'Enter the organization, sites, critical functions, contacts, and required recovery information.',
                'Complete the template or service workspace and review the draft.',
                'Resolve validation findings, approve, and publish through the request.'
            ], 'Accepted requests offer dedicated workspaces for **S-13 Crisis Management**, **S-14 Business Resiliency**, and **S-16 Emergency Preparedness**.']
        ]),
    'suite-trm': guide('Suite Guide: Travel Risk Management',
        'Prepare a reviewed travel workspace with itinerary, assessment, and monitoring.', [
            ['Prepare and release a travel brief', [
                'Accept the **TRM-01** request in **Service Requests**.',
                'Open **TRM-01 Travel Security Workspace**.',
                'Review the traveller, trip window, every travel leg, accommodation, medical providers, and emergency contacts.',
                'Check the draft assessment, evidence, and monitoring coverage.',
                'Correct missing information and complete validation and approval.',
                'Publish reviewed work through the request.',
                'Check the client’s **Travel Brief Dashboard** and available PDFs.'
            ], 'Assigned internal security professionals prepare available forms. Consultants still review and publish. Check monitoring status before expecting alerts.']
        ]),
    'suite-ep': guide('Suite Guide: Executive Protection',
        'Prepare protection projects, advance surveys, and reviewed operational documents.', [
            ['Choose the protection workflow', [
                'Open the accepted request in the Management Portal.',
                'Use **EP Project Hub** for a project, or the advance survey builder for a site survey.',
                'Check the principal, event, dates, full venue addresses, journeys, and known threats.',
                'Review the draft research and complete the required phases.',
                'Resolve validation findings and approve the package before delivery.',
                'Check the client’s **View Project** or delivered survey report after publication.'
            ], 'Updated addresses and new evidence require another review. Complete the After Action Review separately when available.']
        ]),
    'suite-gg': guide('Suite Guide: Geopolitical Analysis',
        'Prepare country intelligence and review available daily country reports.', [
            ['Review or prepare country intelligence', [
                'Open **GeoPolitical Risk & Intelligence** in the working workspace.',
                'Select the country and available intelligence workflow.',
                'Check the report date, coverage, evidence, and information gaps.',
                'For a requested service, complete the corresponding intelligence workspace and review its draft.',
                'Validate and approve before publishing through the request.',
                'Check the country card, daily report, or delivered product visible to the client.'
            ], 'Use the displayed date and availability to judge freshness. Missing or older reports are not new daily updates.']
        ]),
    'suite-gsoc': guide('Suite Guide: Enterprise Control Center',
        'Review the operational picture and follow up on recorded risks.', [
            ['Review the operational picture', [
                'Open the Enterprise Control Center from the working workspace.',
                'Check the organization context and summary indicators.',
                'Review the map, registered assets, intelligence, and report times.',
                'Open a relevant framework or risk to read supporting details.',
                'Record follow-up work through the framework or service request.'
            ], 'Check the underlying record before treating a summary or feed item as an approved finding.']
        ]),
    'suite-lms': guide('Suite Guide: Learning Management Suite (LMS)',
        'Open training and use the learning tools available to your role.', [
            ['Use the learning suite', [
                'Open **Learning Management Suite** from the left navigation.',
                'Select the course, level, or module available to your account.',
                'Choose a lesson and review its content and available media.',
                'Use assessment or trainer controls when provided for your role.',
                'Return to the curriculum to continue or check progress.'
            ], 'Client learning access is available to owners, administrators, trainers, and learners. Update roles through organization user management when needed.']
        ]),
    'help_guidance': guide('Platform Guide: Help & Guidance',
        'Find task instructions, contact support, and ask the workspace assistant.', [
            ['Get help with a task', [
                'Open **Help & Guidance** in the left navigation.',
                'Select **Help Library** and search for the task or screen.',
                'Follow the numbered instructions in the selected topic.',
                'Use **Industry Help & Support** for specialist advice, or **Application IT Help & Support** for a broken feature.',
                'Use consultant chat for an existing client conversation.',
                'Use **AI Chat** to locate workflows, and **Feedback** for suggestions.'
            ], 'Admin instructions cover preparation and management tasks. Client instructions follow the client’s available screens.']
        ]),
    'wf-strategic_reporting_hub': guide('Workflow: Strategic Reporting Hub',
        'Prepare a report using available templates and source material.', [
            ['Prepare and save a report', [
                'Open the Report Hub or required reporting tool.',
                'Choose the report type and scope.',
                'Complete the required period, source, and methodology selections.',
                'Generate the draft and check findings against the selected sources.',
                'Save the reviewed report to the organization.',
                'Use its export control for a PDF when needed.',
                'For client delivery, select the saved report in the service request and publish it.'
            ]]
        ]),
    'wf-global_risk_register': guide('Workflow: Global Risk Register Oversight',
        'Review organization-wide risks and their supporting frameworks.', [
            ['Review and follow up on risks', [
                'Open **Master Risk Register** from the organization dashboard.',
                'Check the active, high-priority, pending, and mitigated counts.',
                'Search or filter the risk entries.',
                'Read each description, score, status, and remediation path.',
                'Open the relevant framework to review pending findings and update risks using available controls.',
                'Check the resulting entry after saving.'
            ], 'Client access shows recorded risks without the working workspace’s editing controls.']
        ]),
    'srm-lifecycle': guide('Guide: SRM Framework Lifecycle',
        'Follow the displayed framework status from setup and review through conclusion or archive.', [
            ['Prepare and activate', [
                'Open the framework in **SRM Command Center**.',
                'Check setup progress and required components in **Draft** or **Review**.',
                'Complete missing inputs and review each component’s findings.',
                'Review pending risks and resolve the dashboard’s next steps.',
                'Use the available approval or activation control when ready.',
                'Confirm **Active** status before using it as the operational framework.'
            ], 'Readiness and risk information are separate. Follow the values and requirements shown by the current dashboard.'],
            ['Conclude, archive, or decommission', [
                'Review outstanding actions and recorded risks.',
                'Use the available conclusion control when finished and read its confirmation.',
                'Confirm **Concluded** status and check the conclusion report download.',
                'Use the archive control to move a record to the archived list.',
                'Read the retention notice and confirm the intended framework.',
                'Use **Decommission** only when authorized and follow its export and confirmation steps.'
            ], 'Conclusion locks the framework. Decommissioning permanently removes records; review the confirmation and export options carefully.']
        ]),
    'srm-risk-register': guide('Guide: SRM Risk Register & Threat Scoring',
        'Review proposed scenarios, add risks, and track mitigation.', [
            ['Review a risk', [
                'Open the framework’s **Risk Register**.',
                'Read pending scenarios and their sources before accepting or dismissing them.',
                'Use **Add Risk** for a manual record.',
                'Complete the requested threat, category, likelihood, impact, and mitigation details.',
                'Save and check the calculated score and status.',
                'Follow the available **Lifecycle Actions** as mitigation progresses.',
                'Record completion through the available action when the goal is achieved.'
            ], 'Risk scores use recorded likelihood and impact. Review displayed framework readiness and compliance separately.']
        ]),
    'ep-workflows': guide('Guide: Executive Protection Workflow Suite',
        'Prepare advance surveys, project packages, and event protection work.', [
            ['Complete an advance survey', [
                'Accept the survey request and open the survey builder.',
                'Check the event, full venue address, route origin, dates, and security details.',
                'Review current research, routes, medical options, and proposed controls.',
                'Review ground checks and the client’s evidence or limitations.',
                'Resolve validation findings and complete approval.',
                'Publish the reviewed survey package through the request.'
            ], 'Unfinished checks remain visible in the report. Revised intake or evidence needs another review.'],
            ['Complete an EP project', [
                'Accept the EP Project Planner request and open **EP Project Hub**.',
                'Confirm essential details, itinerary, venue addresses, team, and contacts.',
                'Work through the project phases and review drafted operations documents.',
                'Validate and approve the operations package before publication.',
                'Check the client’s **Project overview** and **Operations package**.',
                'After the operation, complete and publish the reviewed **After Action Review** when available.',
                'Read client observations and feedback before closing follow-up work.'
            ]]
        ]),
    'suite-hsse': guide('Suite Guide: Health, Safety, Security & Environment (HSSE)',
        'Prepare site safety and compliance work from documented conditions and evidence.', [
            ['Prepare an audit or report', [
                'Open the HSSE workflow or accepted **S-15 HSSE Compliance Audit** request.',
                'Confirm the site, jurisdiction, audit scope, and evidence.',
                'Complete the requested incident, control, or compliance details.',
                'Generate the draft and review findings against the supplied information.',
                'Resolve evidence gaps and validation findings.',
                'Approve and publish through the request for client delivery.'
            ], 'Ask the client for missing documents through the request conversation.']
        ]),
    'enterprise-resiliency': guide('Guide: Enterprise Resiliency Hub',
        'Build crisis, resiliency, and preparedness plans.', [
            ['Build and review a plan', [
                'Open the hub and choose **Crisis Management**, **Business Resiliency**, or **Preparedness**.',
                'Enter the organization, sites, critical functions, responsibilities, and recovery information.',
                'Complete the selected builder’s sections.',
                'Use its compile control and review the template.',
                'For client work, open the accepted **S-13**, **S-14**, or **S-16** workspace and finish review.',
                'Approve and publish through the service request.'
            ], 'Check contacts, site details, and recovery assumptions with the client before release.']
        ]),
    'global-risk-map': guide('Guide: Global Risk Map and Country Intelligence',
        'Use available country reports and map information while checking their dates.', [
            ['Inspect country coverage', [
                'Open the risk map or select a country card on the dashboard.',
                'Read its risk label and available report information.',
                'Open the country report when available.',
                'Check the date, evidence, and coverage before using the findings.',
                'Use **Past briefs** to compare earlier daily reports.',
                'Ask for an updated assessment when coverage does not support the decision.'
            ], 'Use the current map legend. Missing data or an older report is not a low-risk finding.']
        ]),
    'subscription-management': guide('Guide: MSA, Billing & Invoices',
        'Review the current agreement and billing state before advising a client.', [
            ['Review account billing', [
                'Open the organization’s agreement and subscription information in the Management Portal.',
                'Review the signed MSA, dates, pricing, consultant, and subscription or trial status.',
                'Check **Billing & Invoices** in the owner or administrator client preview.',
                'Confirm annual pricing, balances, and **View Signed MSA** availability.',
                'Review service invoices through **Service Requests**, then **Invoices**.',
                'Direct clients to their available invoice and payment controls.'
            ], 'Test Mode needs no subscription, but services may still be billed. Client previews disable payment and cancellation changes.']
        ]),
    'organisation-settings': guide('Guide: Organization Settings & Team Management',
        'Keep organization details and permissions aligned with actual responsibilities.', [
            ['Update client access', [
                'Open **Organizations**, select **Manage**, and review the profile.',
                'Update relevant details and select **Save Organization**.',
                'Update **Organization Users** through the available invitation and editing controls.',
                'Check each user’s role, active status, and separately granted service-request access.',
                'Owners and administrators manage billing and **My Account**; authorized requesters use **Service Requests**.',
                'Owners, administrators, trainers, and learners use the learning suite.',
                'Check the client preview after saving.'
            ], 'Signed-in client owners and administrators use **My Account** for invitations and delivery responsibility.']
        ]),
    'pdf-report-generation': guide('Guide: Report Review, PDF Export & Client Delivery',
        'Check reports before exporting them or releasing them to clients.', [
            ['Export and check a report', [
                'Open the reviewed report in the correct organization.',
                'Check the title, location, dates, scope, and approval details.',
                'Use the available PDF, print, or download control.',
                'Wait for the export and open the resulting file.',
                'Check every page for complete text, readable tables, maps, and correct client details.',
                'Save the reviewed report where the workflow requires it.',
                'Publish through the service request for client delivery.'
            ], 'Exports and publication are separate steps. Downloading alone does not release a report to the client.'],
            ['Recover a failed download', [
                'Check whether the browser blocked a new tab or download.',
                'Allow the site’s download and try the export again.',
                'Confirm the report or reviewed export is available before retrying.',
                'Contact **Application IT Help & Support** with the reference and error if it still fails.'
            ], 'Keep unsaved work before refreshing a page.']
        ])
};
